import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "juristube_admin_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// `withCredentials: true` ensures the HttpOnly access_token cookie set
// by /api/users/login and /api/auth/google/session is forwarded on
// every authenticated request (e.g. /professor/me/eligibility,
// /collections, /users/me/avatar). Without it, anything not coming
// through `authClient` (in /app/frontend/src/lib/auth.jsx) hit 401
// after a successful login — observed in production on /meu-portfolio.
export const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Iter155 — Retry automático em erros transitórios (5xx, 0 = network
// error, ECONNABORTED). Resolve dois sintomas relatados em produção:
//   1. Cloudflare 520 "could not parse response" pontual durante boot
//      ou reciclagem de pod (origem vazia/headers malformados).
//   2. Resposta vazia/curta logo após restart do backend, antes do
//      pool do Motor estabilizar.
// Apenas GETs idempotentes. POST/PUT/DELETE NÃO é re-tentado para
// evitar duplo-write (ex.: importar episódio duas vezes).
//
// Cuidado: 401/403/404/422 são erros legítimos — nunca retentar.
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const method = (config.method || "get").toLowerCase();
    const status = error.response?.status;
    const isTransient = (
      !status ||              // network error / no response (e.g. Cloudflare drop)
      status === 502 ||
      status === 503 ||
      status === 504 ||
      status === 520 ||       // Cloudflare "origin returned malformed"
      status === 521 ||
      status === 522 ||
      status === 524
    );
    config.__retryCount = config.__retryCount || 0;
    const MAX_RETRIES = 2;
    if (method === "get" && isTransient && config.__retryCount < MAX_RETRIES && !config.__noRetry) {
      config.__retryCount += 1;
      const delay = 600 * config.__retryCount;  // 600ms, 1200ms
      await new Promise((r) => setTimeout(r, delay));
      return client(config);
    }
    return Promise.reject(error);
  },
);

export const api = {
  listPodcasts: ({ include_hidden = false } = {}) =>
    client.get(include_hidden ? "/podcasts?include_hidden=true" : "/podcasts").then((r) => r.data),
  listTags: () => client.get("/tags").then((r) => r.data),
  getStats: () => client.get("/stats").then((r) => r.data),
  listAuthors: (params = {}) => client.get("/authors", { params }).then((r) => r.data),
  scientificRanking: (limit = 100) =>
    client.get("/authors/scientific-ranking", { params: { limit } }).then((r) => r.data),
  authors: () => client.get("/authors").then((r) => r.data),
  // Create a minimal author "stub" inline (admin only) — used by the
  // book editing modal to register a co-author without leaving the
  // screen. Idempotent on slug.
  createAuthorStub: (body) =>
    client.post("/admin/authors", body).then((r) => r.data),
  // Columnist → catalog author alias linking (admin only)
  listOrphanColumnists: () => client.get("/admin/colunistas/orphan-authors").then((r) => r.data),
  linkColumnistAlias: (columnist_slug, author_slug) =>
    client.post("/admin/columnist-aliases", { columnist_slug, author_slug }).then((r) => r.data),
  unlinkColumnistAlias: (columnist_slug, author_slug) =>
    client.delete("/admin/columnist-aliases", {
      params: { columnist_slug, author_slug },
    }).then((r) => r.data),
  suggestColumnistAliases: (columnist_slugs = null) =>
    client.post(
      "/admin/columnist-aliases/suggest",
      columnist_slugs ? { columnist_slugs } : {},
      { timeout: 90000 },
    ).then((r) => r.data),
  promoteColumnistToAuthor: (columnist_slug, name = null) =>
    client.post("/admin/columnist-authors/promote", { columnist_slug, name }).then((r) => r.data),
  author: (slug) => client.get(`/authors/${encodeURIComponent(slug)}`).then((r) => r.data),
  // Citation graph: lista os artigos do acervo que citam o autor.
  // Usado pelo card "Citado em N artigos" na página /autor/<slug>.
  authorCitedBy: (slug, limit = 50) =>
    client.get(`/authors/${encodeURIComponent(slug)}/cited-by`, { params: { limit } })
      .then((r) => r.data),
  authorSharedPdfs: (slug) => client.get(`/authors/${encodeURIComponent(slug)}/shared-pdfs`).then((r) => r.data),
  // Iter144 — Rádio JurisTube por autor. Backend filtra db.radio_channels
  // por ``author_slug``/``author_id`` e devolve os últimos N episódios
  // publicados desses canais. Estrutura: ``{channels, episodes, total_episodes}``.
  // Endpoint público — não requer autenticação.
  authorRadioEpisodes: (slug, limit = 10) =>
    client.get(`/authors/${encodeURIComponent(slug)}/radio-episodes`, { params: { limit } })
      .then((r) => r.data),
  updateAuthor: (slug, body) => client.put(`/authors/${slug}`, body).then((r) => r.data),
  // Iter277 — Sincroniza publicações do ORCID (public API v3.0).
  // Nunca sobrescreve obras já curadas com o mesmo DOI.
  syncOrcid: (slug) => client.post(`/admin/authors/${slug}/sync-orcid`),
  deleteAuthor: (slug) => client.delete(`/authors/${slug}`).then((r) => r.data),
  mergeAuthors: (from_slug, into_slug) => client.post("/authors/merge", { from_slug, into_slug }).then((r) => r.data),
  approveAuthorPendingArticles: (slug) => client.post(`/admin/authors/${slug}/approve-pending-articles`).then((r) => r.data),
  resyncAuthorCitations: (slug) => client.post(`/admin/authors/${slug}/resync-citations`).then((r) => r.data),
  listOrphanAuthorSlugs: () => client.get("/admin/authors/orphan-slugs").then((r) => r.data),
  listOrphanDuplicateClusters: () => client.get("/admin/authors/orphan-duplicate-clusters").then((r) => r.data),
  promoteOrphanAuthor: (slug, name) => client.post("/admin/authors/promote-orphan", { slug, name: name || null }).then((r) => r.data),
  rebuildAuthors: () => client.post("/authors/rebuild").then((r) => r.data),
  refreshEpisodeThumbnail: (id) => client.post(`/admin/episodes/${id}/refresh-thumbnail`).then((r) => r.data),
  restoreCustomEpisodeThumbnail: (id) =>
    client.post(`/admin/episodes/${id}/restore-custom-thumbnail`).then((r) => r.data),
  uploadEpisodeThumbnail: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/admin/episodes/${id}/thumbnail-upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  deleteCustomEpisodeThumbnail: (id) =>
    client.delete(`/admin/episodes/${id}/thumbnail-custom`).then((r) => r.data),
  normalizeThumbnails: (dry_run = false) => client.post(`/admin/episodes/normalize-thumbnails${dry_run ? "?dry_run=true" : ""}`).then((r) => r.data),
  backfillSourceText: (params = {}) => {
    const q = new URLSearchParams();
    if (params.dry_run) q.set("dry_run", "true");
    if (params.overwrite) q.set("overwrite", "true");
    return client.post(`/admin/episodes/backfill-source-text?${q.toString()}`).then((r) => r.data);
  },
  backfillDescriptions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.dry_run) q.set("dry_run", "true");
    if (params.force) q.set("force", "true");
    if (params.limit) q.set("limit", String(params.limit));
    return client.post(`/admin/episodes/backfill-descriptions?${q.toString()}`).then((r) => r.data);
  },
  // Author bookshelf — "Obras do autor"
  listAuthorBooks: (slug) =>
    client.get(`/authors/${encodeURIComponent(slug)}/books`).then((r) => r.data),
  // Global library (Livraria)
  listAllBooks: (params = {}) =>
    client.get("/books", { params }).then((r) => r.data),
  // Iter302.6 — Amostra uniforme do acervo completo (usa $sample).
  // Alimenta a rotação por visita da /livraria cobrindo todos os
  // ~909 livros aprovados, não só os primeiros 144.
  randomBooks: (size = 48) =>
    client.get("/books/random", { params: { size } }).then((r) => r.data),
  booksFacets: () => client.get("/books/facets").then((r) => r.data),
  trackBookClick: (bookId) =>
    client.post(`/books/${encodeURIComponent(bookId)}/click`).then((r) => r.data).catch(() => {}),
  createAuthorBook: (slug, body) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/books`, body).then((r) => r.data),
  updateBook: (bookId, body) =>
    client.put(`/admin/books/${encodeURIComponent(bookId)}`, body).then((r) => r.data),
  deleteBook: (bookId) =>
    client.delete(`/admin/books/${encodeURIComponent(bookId)}`).then((r) => r.data),
  updateColumnistAuthors: (articleId, author_slugs) =>
    client.put(`/admin/colunistas/${encodeURIComponent(articleId)}/authors`,
      { author_slugs }).then((r) => r.data),
  uploadBookCover: (bookId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/admin/books/${encodeURIComponent(bookId)}/cover`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  // ----- Amazon book scanner (admin) -----
  amazonScanStatus: () =>
    client.get("/admin/books/amazon-scan/status").then((r) => r.data),
  amazonScanStart: ({ limit = 30, delay_min_seconds = 8, delay_max_seconds = 15, start_after_slug = null } = {}) =>
    client.post("/admin/books/amazon-scan/start", {
      limit, delay_min_seconds, delay_max_seconds, start_after_slug,
    }).then((r) => r.data),
  amazonScanResetCursor: () =>
    client.post("/admin/books/amazon-scan/reset-cursor").then((r) => r.data),
  amazonScanCancel: () =>
    client.post("/admin/books/amazon-scan/cancel").then((r) => r.data),
  amazonScanSkipCurrent: () =>
    client.post("/admin/books/amazon-scan/skip-current").then((r) => r.data),
  amazonScanSingleAuthor: ({ name, slug } = {}) =>
    client.post("/admin/books/amazon-scan/single", { name, slug }).then((r) => r.data),
  amazonScanHealth: () =>
    client.get("/admin/books/amazon-scan/health").then((r) => r.data),
  adminAnalyticsPageviews: (period = "month", limit = 20) =>
    client.get("/admin/analytics/pageviews", { params: { period, limit } }).then((r) => r.data),
  listPendingBooks: (params = {}) =>
    client.get("/admin/books/pending", { params }).then((r) => r.data),
  approvePendingBook: (bookId, body) =>
    client.post(`/admin/books/${encodeURIComponent(bookId)}/approve`, body).then((r) => r.data),
  rejectPendingBook: (bookId) =>
    client.post(`/admin/books/${encodeURIComponent(bookId)}/reject`).then((r) => r.data),
  bulkRejectPendingBooks: (ids) =>
    client.post("/admin/books/bulk-reject", { ids }).then((r) => r.data),
  // Iter306.4 — Re-executa fuzzy match de coautores em livro pendente
  // (usado após cadastro de novo professor).
  rescanBookCoauthors: (bookId) =>
    client.post(`/admin/books/${encodeURIComponent(bookId)}/rescan-coauthors`).then((r) => r.data),
  // Iter306.5 — Fila colaborativa de sugestões de coautoria.
  createCoauthorSuggestion: (payload) =>
    client.post("/coauthor-suggestions", payload).then((r) => r.data),
  listCoauthorSuggestions: (params = {}) =>
    client.get("/admin/coauthor-suggestions", { params }).then((r) => r.data),
  countCoauthorSuggestions: () =>
    client.get("/admin/coauthor-suggestions/count").then((r) => r.data),
  approveCoauthorSuggestion: (sid) =>
    client.post(`/admin/coauthor-suggestions/${encodeURIComponent(sid)}/approve`).then((r) => r.data),
  rejectCoauthorSuggestion: (sid) =>
    client.post(`/admin/coauthor-suggestions/${encodeURIComponent(sid)}/reject`).then((r) => r.data),
  manualAddBook: (body) =>
    client.post("/admin/books/manual", body).then((r) => r.data),
  adminEmailAudit: ({ limit = 50, email = null } = {}) => {
    const params = { limit };
    if (email) params.email = email;
    return client.get("/admin/email/audit", { params }).then((r) => r.data);
  },
  // ── Acervo Histórico de Revistas Acadêmicas ──────────────────────
  journalArchiveLanding: () => client.get("/journal-archive").then((r) => r.data),
  journalArchiveList: (journal, { q = "", year = "", sort = "", skip = 0, limit = 40 } = {}) =>
    client.get(`/journal-archive/${encodeURIComponent(journal)}`, { params: { q, year, sort, skip, limit } }).then((r) => r.data),
  journalArchiveArticle: (slug) =>
    client.get(`/journal-archive/article/${encodeURIComponent(slug)}`).then((r) => r.data),
  journalArchiveHtmlUrl: (slug) => `${API}/journal-archive/article/${encodeURIComponent(slug)}/html`,
  journalArchivePdfUrl: (slug) => `${API}/journal-archive/article/${encodeURIComponent(slug)}/pdf`,
  authorJournalArchive: (slug) =>
    client.get(`/authors/${encodeURIComponent(slug)}/journal-archive`).then((r) => r.data),
  // Admin
  jaAdminStats: () => client.get("/admin/journal-archive/stats").then((r) => r.data),
  jaAdminArticles: (params = {}) =>
    client.get("/admin/journal-archive/articles", { params }).then((r) => r.data),
  jaAdminImports: () => client.get("/admin/journal-archive/imports").then((r) => r.data),
  jaImportStatus: (jobId) =>
    client.get(`/admin/journal-archive/import/${jobId}/status`).then((r) => r.data),
  jaUploadInit: (filename, totalChunks) =>
    client.post("/admin/journal-archive/upload/init", { filename, total_chunks: totalChunks }).then((r) => r.data),
  jaUploadChunk: (uploadId, index, blob) => {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", index);
    fd.append("chunk", blob, "chunk");
    return client.post("/admin/journal-archive/upload/chunk", fd).then((r) => r.data);
  },
  jaUploadComplete: (uploadId, { dryRun = false, purgeFirst = false } = {}) =>
    client.post("/admin/journal-archive/upload/complete", { upload_id: uploadId, dry_run: dryRun, purge_first: purgeFirst }).then((r) => r.data),
  jaLinkAuthor: (slug, authorSlug) =>
    client.post(`/admin/journal-archive/articles/${encodeURIComponent(slug)}/link`, { author_slug: authorSlug }).then((r) => r.data),
  jaSuggestions: (params = {}) =>
    client.get("/admin/journal-archive/suggestions", { params }).then((r) => r.data),
  jaConfirmHigh: () =>
    client.post("/admin/journal-archive/suggestions/confirm-high").then((r) => r.data),
  jaRecentLinks: (limit = 200) =>
    client.get("/admin/journal-archive/recent-links", { params: { limit } }).then((r) => r.data),
  jaRecentLinksBackfill: () =>
    client.post("/admin/journal-archive/recent-links/backfill").then((r) => r.data),
  jaUnlinkAuthor: (slug, authorSlug) =>
    client.post(`/admin/journal-archive/articles/${encodeURIComponent(slug)}/unlink`, { author_slug: authorSlug }).then((r) => r.data),
  jaDeleteArticle: (slug) =>
    client.delete(`/admin/journal-archive/articles/${encodeURIComponent(slug)}`).then((r) => r.data),
  jaSettings: () => client.get("/admin/journal-archive/settings").then((r) => r.data),
  jaUpdateSettings: (stripEditionLine) =>
    client.put("/admin/journal-archive/settings", { strip_edition_line: stripEditionLine }).then((r) => r.data),
  jaBackfillBodyText: (batch = 40) =>
    client.post("/admin/journal-archive/backfill-body-text", { batch }).then((r) => r.data),
  jaAuthorSearch: (q) =>
    client.get("/admin/journal-archive/author-search", { params: { q } }).then((r) => r.data),

  // Author articles — Phase 1 internal hosting
  listAuthorArticles: (slug, limit = 200) =>
    client.get(`/authors/${encodeURIComponent(slug)}/articles?limit=${limit}`).then((r) => r.data),
  trackAuthorView: (slug) =>
    client.post(`/authors/${encodeURIComponent(slug)}/track-view`).then((r) => r.data).catch(() => null),
  authorImpactStats: (slug) =>
    client.get(`/authors/${encodeURIComponent(slug)}/impact-stats`).then((r) => r.data),
  getAuthorArticleQuota: (slug) =>
    client.get(`/admin/authors/${encodeURIComponent(slug)}/article-quota`).then((r) => r.data),
  setAuthorArticleQuota: (slug, quota) =>
    client.put(`/admin/authors/${encodeURIComponent(slug)}/article-quota`, { quota }).then((r) => r.data),
  uploadAuthorArticle: (slug, file, { title, sourceUrl, sourceLabel, coAuthorSlugs } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (title) fd.append("title", title);
    if (sourceUrl) fd.append("source_url", sourceUrl);
    if (sourceLabel) fd.append("source_label", sourceLabel);
    if (coAuthorSlugs && coAuthorSlugs.length > 0) {
      fd.append("co_author_slugs", coAuthorSlugs.join(","));
    }
    return client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  importConjurArticle: (slug, url, titleOverride) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/import-conjur`,
      { url, title_override: titleOverride || null }).then((r) => r.data),
  importJotaArticle: (slug, url, titleOverride) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/import-jota`,
      { url, title_override: titleOverride || null }).then((r) => r.data),
  importMigalhasArticle: (slug, url, titleOverride) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/import-migalhas`,
      { url, title_override: titleOverride || null }).then((r) => r.data),
  // Auloteca single-URL importer. Partner site — clean WordPress HTML,
  // auto-detects coauthors listed in the AUTOR(ES) block on the page.
  importAulotecaArticle: (slug, url, titleOverride) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/import-auloteca`,
      { url, title_override: titleOverride || null }).then((r) => r.data),
  // Generic HTML importer — catch-all for any article URL. Editor
  // supplies the reference + coauthors; no slug-mismatch guard.
  importHtmlArticle: (slug, payload) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/import-html`,
      payload).then((r) => r.data),
  // Paste raw HTML or plain text as an article body. Admin path
  // publishes immediately; the professor self-service equivalent
  // (``professorPasteArticle``) goes to the moderation queue.
  pasteAuthorArticle: (slug, payload) =>
    client.post(`/admin/authors/${encodeURIComponent(slug)}/articles/paste`,
      payload).then((r) => r.data),
  // Discovery (read-only): lists every article on the Auloteca author
  // tag page (/tag/t-{slug}/) and marks which URLs are already in the
  // JurisTube acervo. Used by the "Buscar na Auloteca" button.
  listAulotecaArticlesForAuthor: (slug) =>
    client.get(`/admin/auloteca/author-articles/${encodeURIComponent(slug)}`).then((r) => r.data),
  // Full sitemap sweep — fetches every article on Auloteca and buckets
  // them by JurisTube author. Heavy (30-60s), invoked manually from
  // the admin "Varrer Auloteca" panel.
  sweepAulotecaSitemap: () =>
    client.post(`/admin/auloteca/sweep-sitemap`).then((r) => r.data),
  // Auto-import every NEW Auloteca article whose author already exists
  // on JurisTube. Heavy (~1–3 min depending on volume); the backend
  // reuses the single-URL importer so cota/dedupe/coauthors all match.
  autoImportAulotecaKnown: (params = {}) =>
    client.post(`/admin/auloteca/auto-import-known`, null, { params }).then((r) => r.data),
  // Batch import — discovers and imports every article from a Conjur/JOTA listing
  batchImportArticles: (body) =>
    client.post(`/admin/articles/batch-import`, body).then((r) => r.data),
  getImportJob: (jobId) =>
    client.get(`/admin/articles/import-jobs/${encodeURIComponent(jobId)}`).then((r) => r.data),
  listImportJobs: (params = {}) =>
    client.get(`/admin/articles/import-jobs`, { params }).then((r) => r.data),
  // Iter309b — Backfill em massa do Conjur (usa a nova rota via WP REST).
  conjurBackfillStart: () =>
    client.post(`/admin/articles/conjur-backfill`).then((r) => r.data),
  conjurBackfillLatest: () =>
    client.get(`/admin/articles/conjur-backfill/latest`).then((r) => r.data),
  // Iter309d — Backfill em massa do JOTA (parser __NEXT_DATA__).
  jotaBackfillStart: () =>
    client.post(`/admin/articles/jota-backfill`).then((r) => r.data),
  jotaBackfillLatest: () =>
    client.get(`/admin/articles/jota-backfill/latest`).then((r) => r.data),
  // Iter309e — Cancelar backfill em andamento.
  conjurBackfillCancel: () =>
    client.post(`/admin/articles/conjur-backfill/cancel`).then((r) => r.data),
  jotaBackfillCancel: () =>
    client.post(`/admin/articles/jota-backfill/cancel`).then((r) => r.data),
  // Iter309f — Retry por autor dentro de um parent backfill.
  backfillRetryAuthor: ({ parent_batch_id, author_slug }) =>
    client.post(`/admin/articles/backfill/retry-author`, {
      parent_batch_id, author_slug,
    }).then((r) => r.data),
  // Iter309g/h — Auditoria e purga de artigos Conjur misatribuídos.
  conjurAuditAttribution: (body) =>
    client.post(`/admin/articles/conjur/audit-attribution`, body).then((r) => r.data),
  conjurPurgeRecent: (body) =>
    client.post(`/admin/articles/conjur/purge-recent`, body).then((r) => r.data),
  // Iter312 — Limpeza retroativa de mídia decorativa nos imports.
  stripImportedMedia: (body) =>
    client.post(`/admin/colunistas/strip-media`, body).then((r) => r.data),
  // Iter313 — Relatório de captura: frescor/atividade dos robôs por fonte.
  importsCaptureHealth: () =>
    client.get(`/admin/imports/capture-health`).then((r) => r.data),
  // Iter315 — Gatilho manual de descoberta por fonte (jota/migalhas/conjur).
  discoverSourceNow: (source) =>
    client.post(`/admin/articles/${source}/discover-now`).then((r) => r.data),
  // Iter316 — Export .zip das transcrições de um podcast (retorna a
  // resposta completa para ler o blob + header X-Episode-Count).
  exportPodcastTranscriptsZip: (slug) =>
    client.get(`/admin/podcasts/${slug}/transcripts.zip`, { responseType: "blob" }),
  // Iter314 — Duplicatas de artigos (varredura read-only + merge seguro 301).
  scanDuplicateArticles: (skip = 0, limit = 80) =>
    client.get(`/admin/articles/duplicate-scan`, { params: { skip, limit } }).then((r) => r.data),
  dedupeApplyArticles: (body) =>
    client.post(`/admin/articles/dedupe-apply`, body).then((r) => r.data),
  dedupeAutoArticles: (skip = 0, limit = 60) =>
    client.post(`/admin/articles/dedupe-auto`, null, { params: { skip, limit } }).then((r) => r.data),
  backfillContentHash: (limit = 150) =>
    client.post(`/admin/articles/backfill-content-hash`, null, { params: { limit } }).then((r) => r.data),
  setAuthorListingUrls: (slug, body) =>
    client.put(`/admin/authors/${encodeURIComponent(slug)}/listing-urls`, body).then((r) => r.data),
  syncColumnistsNow: () =>
    client.post(`/admin/articles/sync-columnists-now`).then((r) => r.data),
  getColumnistSyncStatus: () =>
    client.get(`/admin/articles/sync-status`).then((r) => r.data),
  sendDigestNow: () =>
    client.post(`/admin/articles/sync-digest/send-now`).then((r) => r.data),
  previewDigest: () =>
    client.get(`/admin/articles/sync-digest/preview`).then((r) => r.data),
  // Manual review/edit of imported HTML body (Conjur/JOTA)
  getColumnistAdminDetail: (articleId) =>
    client.get(`/admin/colunistas/${encodeURIComponent(articleId)}`).then((r) => r.data),
  updateColumnistBody: (articleId, contentHtml, opts = {}) =>
    client.put(`/admin/colunistas/${encodeURIComponent(articleId)}/body`, {
      content_html: contentHtml,
      title: opts.title ?? null,
      excerpt: opts.excerpt ?? null,
    }).then((r) => r.data),
  // Lightweight rename — works for ANY article source (PDF/DOCX upload
  // AND Conjur/JOTA HTML imports), unlike the body editor.
  updateColumnistTitle: (articleId, title) =>
    client.put(`/admin/colunistas/${encodeURIComponent(articleId)}/title`, {
      title,
    }).then((r) => r.data),
  updateColumnistSource: (articleId, { source_venue, source_url }) =>
    client.put(`/admin/colunistas/${encodeURIComponent(articleId)}/source`, {
      source_venue,
      source_url,
    }).then((r) => r.data),
  // Bulk rename — preview when ``dry_run:true``, persist when false.
  bulkRenameArticles: (payload) =>
    client.post("/admin/colunistas/bulk-rename", payload).then((r) => r.data),
  // -------------------- Tags merge / duplicates --------------------
  listAllTags: () => client.get("/admin/tags/all").then((r) => r.data),
  listDuplicateTags: () => client.get("/admin/tags/duplicates").then((r) => r.data),
  mergeTags: (payload) => client.post("/admin/tags/merge", payload).then((r) => r.data),
  // -------------------- Article auto-tagger --------------------
  autoTagStats: () => client.get("/admin/articles/auto-tag/stats").then((r) => r.data),
  autoTagRun: (payload) => client.post("/admin/articles/auto-tag", payload).then((r) => r.data),
  // Related articles by shared tags
  getColumnistArticleRelated: (author, slug, limit = 3) =>
    client.get(`/colunistas/${author}/${slug}/related`, { params: { limit } })
      .then((r) => r.data),
  recleanJotaArticles: () =>
    client.post(`/admin/articles/reclean-jota`).then((r) => r.data),
  scanExternalPages: (body) =>
    client.post(`/admin/authors/scan-external-pages`, body || {}).then((r) => r.data),
  externalPagesScanStatus: () =>
    client.get(`/admin/authors/scan-status`).then((r) => r.data),
  listExternalPages: () =>
    client.get(`/admin/authors/external-pages`).then((r) => r.data),
  dedupeArticlesByUrl: () =>
    client.post(`/admin/articles/dedupe-by-url`).then((r) => r.data),
  recleanConjurSpacca: () =>
    client.post(`/admin/articles/reclean-conjur-spacca`).then((r) => r.data),
  // Iter307.11 — Re-baixa 1 artigo importado (Conjur/JOTA/Migalhas/
  // Auloteca) e reprocessa com o parser atual. Útil quando o site
  // muda de layout e o corpo importado ficou truncado / poluído.
  refetchImportedArticle: (articleId, overwriteMeta = true) =>
    client.post(
      `/admin/articles/${articleId}/refetch?overwrite_meta=${overwriteMeta ? "true" : "false"}`,
    ).then((r) => r.data),
  // Iter307.11 — Varredura em massa: encontra e re-baixa artigos
  // importados com corpo suspeitosamente curto.
  refetchBrokenImports: (opts = {}) => {
    const params = new URLSearchParams({
      source: opts.source || "conjur_import",
      min_bytes: String(opts.minBytes ?? 800),
      dry_run: opts.dryRun === false ? "false" : "true",
      overwrite_meta: opts.overwriteMeta === false ? "false" : "true",
      limit: String(opts.limit ?? 200),
    });
    return client.post(`/admin/articles/refetch-broken?${params.toString()}`)
      .then((r) => r.data);
  },
  // Iter307.13 — Import health summary por site de origem.
  importsHealth: () =>
    client.get("/admin/imports/health").then((r) => r.data),
  // Iter307.14 — Limpa `import_body_short` de artigos que hoje passam
  // no threshold (rodar após deploy do parser Elementor Iter307.11).
  clearStaleBodyShort: (minBytes = 800) =>
    client.post(`/admin/articles/clear-stale-body-short?min_bytes=${minBytes}`).then((r) => r.data),

  // ================================================================
  // Iter308 — Gestão dinâmica da árvore acadêmica.
  // ================================================================
  taxonomyGet: () => client.get("/admin/taxonomy").then((r) => r.data),
  taxonomyStats: () => client.get("/admin/taxonomy/stats").then((r) => r.data),
  taxonomyCreate: ({ name, parent_id, blurb }) =>
    client.post("/admin/taxonomy/node", { name, parent_id: parent_id || null, blurb: blurb || null })
      .then((r) => r.data),
  taxonomyEdit: (nodeId, { name, blurb }) =>
    client.patch(`/admin/taxonomy/node/${nodeId}`, { name, blurb }).then((r) => r.data),
  taxonomyDelete: (nodeId) =>
    client.delete(`/admin/taxonomy/node/${nodeId}`).then((r) => r.data),
  taxonomyRestore: (nodeId) =>
    client.post(`/admin/taxonomy/node/${nodeId}/restore`).then((r) => r.data),
  taxonomyArchive: (nodeId) =>
    client.post(`/admin/taxonomy/node/${nodeId}/archive`).then((r) => r.data),
  taxonomyUnarchive: (nodeId) =>
    client.post(`/admin/taxonomy/node/${nodeId}/unarchive`).then((r) => r.data),
  // Iter309o — Mover nó na árvore (reparent). new_parent_id=null → vira raiz.
  taxonomyMove: (nodeId, newParentId) =>
    client.post(`/admin/taxonomy/node/${nodeId}/move`,
      { new_parent_id: newParentId || null }).then((r) => r.data),
  purgeJotaNewsletters: (dryRun = false) =>
    client.post(`/admin/articles/purge-jota-newsletters${dryRun ? "?dry_run=true" : ""}`).then((r) => r.data),
  // -------- Suspicious-import linter (v3.21) --------
  listSuspiciousArticles: (limit = 200) =>
    client.get(`/admin/articles/suspicious`, { params: { limit } }).then((r) => r.data),
  markArticleReviewed: (articleId) =>
    client.post(`/admin/articles/${encodeURIComponent(articleId)}/mark-reviewed`).then((r) => r.data),
  lintExistingArticles: (dryRun = false, body = null) =>
    client.post(`/admin/articles/lint-existing${dryRun ? "?dry_run=true" : ""}`, body || {}).then((r) => r.data),
  // -------- Per-author source blocklist + bulk-purge (v3.23) --------
  setBlockedImportSources: (slug, blocked_import_sources) =>
    client.put(`/admin/authors/${encodeURIComponent(slug)}/blocked-sources`, {
      blocked_import_sources,
    }).then((r) => r.data),
  purgeAuthorArticlesBySource: (slug, source, dryRun = false) =>
    client.post(
      `/admin/authors/${encodeURIComponent(slug)}/articles/purge-source`,
      null,
      { params: { source, dry_run: dryRun } },
    ).then((r) => r.data),

  // -------- Professor self-service (approved professors with own page) --
  professorEligibility: () => client.get("/professor/me/eligibility").then((r) => r.data),
  // Iter306 — Perfil público (db.authors) do professor logado, para o
  // formulário de auto-atualização em /minhas-colecoes.
  professorAuthorProfile: () => client.get("/professor/me/author-profile").then((r) => r.data),
  professorUpdateAuthorProfile: (payload) =>
    client.put("/professor/me/author-profile", payload).then((r) => r.data),
  professorUploadArticle: (formData) =>
    client.post("/professor/me/articles/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
  professorImportConjur: (url, title_override) =>
    client.post("/professor/me/articles/import-conjur", { url, title_override }).then((r) => r.data),
  professorImportJota: (url, title_override) =>
    client.post("/professor/me/articles/import-jota", { url, title_override }).then((r) => r.data),
  professorImportHtml: (payload) =>
    client.post("/professor/me/articles/import-html", payload).then((r) => r.data),
  professorPasteArticle: (payload) =>
    client.post("/professor/me/articles/paste", payload).then((r) => r.data),
  professorSuggestBook: (payload) =>
    client.post("/professor/me/books/suggest", payload).then((r) => r.data),
  professorPending: () => client.get("/professor/me/pending").then((r) => r.data),
  // Iter307.6 — Reenvio do artigo rejeitado pelo próprio autor.
  // Recebe FormData com title (obrigatório) e file (opcional).
  professorResubmitArticle: (articleId, formData) =>
    client.put(`/professor/me/articles/${articleId}/resubmit`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
  professorStats: () => client.get("/professor/me/stats").then((r) => r.data),
  professorLattesExport: () => client.get("/professor/me/lattes-export").then((r) => r.data),
  // Termo de autorização de incorporação de obras (Jun/2026)
  workAuthStatus: () => client.get("/professor/me/work-authorization").then((r) => r.data),
  workAuthAccept: () => client.post("/professor/me/work-authorization/accept").then((r) => r.data),
  workAuthRevoke: (note) =>
    client.post("/professor/me/work-authorization/revoke", { note: note || null }).then((r) => r.data),
  workAuthRequestAction: (payload) =>
    client.post("/professor/me/work-authorization/request-work-action", payload).then((r) => r.data),

  // -------- Admin moderation queue --------
  moderationQueue: () => client.get("/admin/moderation/queue").then((r) => r.data),
  moderationPendingCount: () => client.get("/admin/moderation/pending-count").then((r) => r.data),
  approveArticle: (id) => client.post(`/admin/moderation/articles/${id}/approve`).then((r) => r.data),
  // Iter307.5 — reject com feedback opcional. `soft=true` preserva o doc e
  // manda e-mail; `soft=false` mantém o comportamento legado destrutivo.
  rejectArticle: (id, reason, opts = {}) => client.post(
    `/admin/moderation/articles/${id}/reject`,
    { reason: reason || "", soft: opts.soft !== false, notify_author: opts.notify_author !== false },
  ).then((r) => r.data),
  // Iter307.5 — Edita título antes de aprovar (limpa hashes de filename).
  editArticleTitle: (id, title) => client.put(
    `/admin/moderation/articles/${id}/title`, { title },
  ).then((r) => r.data),
  // Iter307.5 — Aprovação em massa (recebe array de IDs).
  bulkApproveArticles: (ids) => client.post(
    "/admin/moderation/articles/bulk-approve", { article_ids: ids || [] },
  ).then((r) => r.data),
  // Iter307.5 — Baixa o PDF pendente como blob (admin-only, com auth
  // no header) e devolve um object URL para abrir em nova aba.
  moderationArticlePreview: (id) => client.get(
    `/admin/moderation/articles/${id}/preview`,
    { responseType: "blob" },
  ).then((r) => URL.createObjectURL(r.data)),
  approveBook: (id) => client.post(`/admin/moderation/books/${id}/approve`).then((r) => r.data),
  rejectBook: (id, reason) => client.post(`/admin/moderation/books/${id}/reject`, { reason: reason || "" }).then((r) => r.data),
  setUserAutoApprove: (userId, on) =>
    client.put(`/admin/users/${userId}/auto-approve`, { auto_approve_uploads: !!on }).then((r) => r.data),
  updateArticlePublishedAt: (articleId, publishedAt) =>
    client.put(`/admin/colunistas/${encodeURIComponent(articleId)}/published-at`, {
      published_at: publishedAt,
    }).then((r) => r.data),
  // Author article delete reuses the columnist row endpoint — both schemas
  // live under db.articles and share the GridFS bucket.
  deleteAuthorArticle: (articleId) =>
    client.delete(`/admin/colunistas/${encodeURIComponent(articleId)}`).then((r) => r.data),
  authorArticleStats: (limit = 200) =>
    client.get(`/admin/authors/article-stats?limit=${limit}`).then((r) => r.data),
  setEpisodePriority: (episodeId, body) =>
    client.post(`/admin/episodes/${encodeURIComponent(episodeId)}/priority`, body).then((r) => r.data),
  setShortPriority: (shortId, body) =>
    client.post(`/admin/shorts/${encodeURIComponent(shortId)}/priority`, body).then((r) => r.data),
  aboutFeatured: () => client.get("/about/featured").then((r) => r.data),
  featureEpisodeOnAbout: (id, featured = true) =>
    client.post(`/admin/episodes/${id}/feature-about`, { featured }).then((r) => r.data),
  featureShortOnAbout: (id, featured = true) =>
    client.post(`/admin/shorts/${id}/feature-about`, { featured }).then((r) => r.data),
  // Stand-alone (non-podcast) episode creation — pulls metadata from YouTube
  // and assigns it to the hidden 'institucional' umbrella.
  createStandaloneEpisode: (body) =>
    client.post("/admin/episodes/standalone", body).then((r) => r.data),
  // Courses
  listCourses: () => client.get("/courses").then((r) => r.data),
  listAllCourses: () => client.get("/courses/all").then((r) => r.data),
  getCourse: (slug) => client.get(`/courses/${slug}`).then((r) => r.data),
  createCourse: (body) => client.post("/courses", body).then((r) => r.data),
  updateCourse: (slug, body) => client.put(`/courses/${slug}`, body).then((r) => r.data),
  deleteCourse: (slug) => client.delete(`/courses/${slug}`).then((r) => r.data),
  // Ads
  getAds: () => client.get("/ads").then((r) => r.data),
  updateAds: (body) => client.put("/ads", body).then((r) => r.data),
  addBanner: (body) => client.post("/ads/banners", body).then((r) => r.data),
  removeBanner: (id) => client.delete(`/ads/banners/${id}`).then((r) => r.data),
  listEpisodes: (params = {}) => client.get("/episodes", { params }).then((r) => r.data),
  getEpisode: (id) => client.get(`/episodes/${id}`).then((r) => r.data),
  getEpisodeBySlug: (podcastSlug, episodeSlug) =>
    client.get(`/episodes/by-slug/${encodeURIComponent(podcastSlug)}/${encodeURIComponent(episodeSlug)}`).then((r) => r.data),
  // Article ↔ video linking (Feb/2026). Main video set by admin + up
  // to 3 tag-derived suggestions. Empty payload returned when none.
  getColumnistArticleRelatedVideos: (authorSlug, slug) =>
    client.get(`/colunistas/${encodeURIComponent(authorSlug)}/${encodeURIComponent(slug)}/related-videos`).then((r) => r.data),
  // Public reverse: articles linked to a given episode. Powers the
  // "Textos relacionados" block on the episode page.
  getEpisodeLinkedArticles: (episodeId) =>
    client.get(`/episodes/${encodeURIComponent(episodeId)}/linked-articles`).then((r) => r.data),
  adminLinkVideoToArticle: (articleId, episodeRef) =>
    client.post(`/admin/articles/${articleId}/link-video`, { episode_ref: episodeRef }).then((r) => r.data),
  adminToggleRelatedVideos: (articleId, disabled) =>
    client.post(`/admin/articles/${articleId}/disable-related`, { disabled }).then((r) => r.data),
  // Reverse flow (episode → articles). Used by EpisodeFormModal's
  // "Artigos vinculados" panel so the admin can attach articles to a
  // video without leaving the episode editor.
  adminListArticlesLinkedToEpisode: (episodeId) =>
    client.get(`/admin/episodes/${episodeId}/linked-articles`).then((r) => r.data),
  adminLinkArticleToEpisode: (episodeId, articleId) =>
    client.post(`/admin/episodes/${episodeId}/link-article`, { article_id: articleId }).then((r) => r.data),
  adminUnlinkArticleFromEpisode: (episodeId, articleId) =>
    client.delete(`/admin/episodes/${episodeId}/link-article/${articleId}`).then((r) => r.data),
  // Lightweight admin search across all articles (autocomplete).
  adminSearchArticles: (q, limit = 12) =>
    client.get(`/admin/articles/search`, { params: { q, limit } }).then((r) => r.data),
  // Admin search across all episodes for the article→video autocomplete.
  // Wraps GET /episodes?q=…&include_pending=true so pending/unlisted
  // videos can also be linked (e.g. for podcasts published unlisted
  // while the team prepares the transcript).
  adminSearchEpisodes: (q, limit = 12) =>
    client.get(`/episodes`, { params: { q, limit, include_pending: true } }).then((r) => r.data),
  // Episode publication scheduler (Feb/2026)
  adminListPendingEpisodes: () => client.get("/admin/episodes/pending").then((r) => r.data),
  adminScheduleEpisode: (episodeId, isoDatetime) =>
    client.post(`/admin/episodes/${episodeId}/schedule`, { scheduled_publish_at: isoDatetime }).then((r) => r.data),
  adminPublishEpisodeNow: (episodeId) =>
    client.post(`/admin/episodes/${episodeId}/publish-now`).then((r) => r.data),
  adminUnpublishEpisode: (episodeId) =>
    client.post(`/admin/episodes/${episodeId}/unpublish`).then((r) => r.data),
  // Iter182 — Publicação em lote: publica vários pendentes de uma vez
  // (ordenados por published_at ASC no servidor, do mais antigo ao mais novo).
  adminBulkPublishEpisodes: (episodeIds) =>
    client.post(`/admin/episodes/bulk-publish`, { episode_ids: episodeIds }).then((r) => r.data),
  // Manual single-video import (Feb/2026) — for unlisted videos the
  // 24h auto-sync hasn't picked up yet. Body: {youtube_url, podcast_slug, scheduled_publish_at?}
  adminImportEpisodeManual: (body) =>
    client.post("/admin/episodes/import-manual", body).then((r) => r.data),
  adminListPodcasts: () => client.get("/podcasts").then((r) => r.data),
  // Author share kit (Feb/2026) — payload includes copy-ready texts +
  // card URL + badge_html for personal site embeds.
  getAuthorSharePayload: (slug) =>
    client.get(`/share/author/${encodeURIComponent(slug)}/payload`).then((r) => r.data),
  registerView: (id) => client.post(`/episodes/${id}/view`).then((r) => r.data).catch(() => null),
  ranking: (period = "month", limit = 5) => client.get(`/ranking/episodes?period=${period}&limit=${limit}`).then((r) => r.data),
  rankingCreators: (period = "month", limit = 5) => client.get(`/ranking/creators?period=${period}&limit=${limit}`).then((r) => r.data),
  getPublicSettings: () => client.get("/settings/public").then((r) => r.data),
  updateSiteSettings: (body) => client.put("/admin/settings", body).then((r) => r.data),
  createEpisode: (body) => client.post("/episodes", body).then((r) => r.data),
  updateEpisode: (id, body) => client.put(`/episodes/${id}`, body).then((r) => r.data),
  deleteEpisode: (id) => client.delete(`/episodes/${id}`).then((r) => r.data),
  // Transcript management — POST accepts multipart (file) OR JSON (text).
  // We expose two helpers so the admin UI doesn't have to juggle FormData
  // construction inline.
  adminSetTranscriptFile: (id, file, source) => {
    const form = new FormData();
    form.append("file", file);
    if (source) form.append("source", source);
    return client
      .post(`/admin/episodes/${id}/transcript`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  adminSetTranscriptText: (id, text, source) =>
    client.post(`/admin/episodes/${id}/transcript`, { text, source }).then((r) => r.data),
  adminDeleteTranscript: (id) =>
    client.delete(`/admin/episodes/${id}/transcript`).then((r) => r.data),
  bulkTag: (episode_ids, add_tags, remove_tags, replace = false) =>
    client.post("/episodes/bulk-tag", { episode_ids, add_tags, remove_tags, replace }).then((r) => r.data),
  bulkDelete: (episode_ids) =>
    client.post("/episodes/bulk-delete", { episode_ids }).then((r) => r.data),
  reparseAuthors: () => client.post("/episodes/reparse-authors").then((r) => r.data),
  stripHashtags: () => client.post("/episodes/strip-hashtags").then((r) => r.data),
  extractAuthorsLLM: () => client.post("/episodes/extract-authors-llm", {}, { timeout: 300000 }).then((r) => r.data),
  exportCsvUrl: () => `${API}/export/episodes.csv`,
  exportAuthorsUrl: () => `${API}/export/authors.csv`,
  exportCollectionsUrl: () => `${API}/export/collections.csv`,
  exportSubscribersUrl: () => `${API}/export/subscribers.csv`,
  exportSuggestionsUrl: () => `${API}/export/suggestions.csv`,
  fetchDurations: () => client.post("/episodes/fetch-durations", {}, { timeout: 300000 }).then((r) => r.data),
  // Suggestions (public create + admin CRUD)
  createSuggestion: (body) => client.post("/suggestions", body).then((r) => r.data),
  listSuggestions: (params = {}) => client.get("/suggestions", { params }).then((r) => r.data),
  suggestionsStats: () => client.get("/suggestions/stats").then((r) => r.data),
  markSuggestion: (id, read) => client.put(`/suggestions/${id}`, { read }).then((r) => r.data),
  deleteSuggestion: (id) => client.delete(`/suggestions/${id}`).then((r) => r.data),
  // Iter297 — resposta ao usuário via ZeptoMail (contato@juristube.com.br)
  suggestionReplyTemplates: () =>
    client.get("/suggestions/reply-templates").then((r) => r.data),
  replySuggestion: (id, payload) =>
    client.post(`/suggestions/${id}/reply`, payload).then((r) => r.data),
  publicCollections: () => client.get("/public-collections").then((r) => r.data),
  getCreator: (user_id) => client.get(`/creators/${user_id}`).then((r) => r.data),
  adminAllCollections: () => client.get("/admin/all-collections").then((r) => r.data),
  adminToggleFeature: (id) => client.post(`/admin/collections/${id}/feature`).then((r) => r.data),
  parseTimestamps: () => client.post("/episodes/parse-timestamps").then((r) => r.data),
  subscribe: (email, source = "footer") => client.post("/newsletter/subscribe", { email, source }).then((r) => r.data),
  listSubscribers: () => client.get("/newsletter/subscribers").then((r) => r.data),
  importYoutube: (body) => client.post("/import/youtube", body).then((r) => r.data),
  importAllPodcasts: () => client.post("/import/youtube/all", {}, { timeout: 600000 }).then((r) => r.data),
  fixPublishedDates: () => client.post("/episodes/fix-published-dates", {}, { timeout: 300000 }).then((r) => r.data),
  getImportStatus: () => client.get("/import/youtube/status").then((r) => r.data),
  stats: () => client.get("/stats").then((r) => r.data),
  createTag: (name) => client.post("/tags", { name }).then((r) => r.data),
  updateTag: (slug, name) => client.put(`/tags/${slug}`, { name }).then((r) => r.data),
  deleteTag: (slug) => client.delete(`/tags/${slug}`).then((r) => r.data),
  createPodcast: (body) => client.post("/podcasts", body).then((r) => r.data),
  updatePodcast: (slug, body) => client.put(`/podcasts/${slug}`, body).then((r) => r.data),
  deletePodcast: (slug) => client.delete(`/podcasts/${slug}`).then((r) => r.data),
  login: (passwordOrCreds) => {
    // Accepts either a bare password string (legacy) or an object
    // { username, password } for the named admin_users flow.
    const body = typeof passwordOrCreds === "string"
      ? { password: passwordOrCreds }
      : passwordOrCreds;
    return client.post("/auth/login", body).then((r) => r.data);
  },
  verify: () => client.get("/auth/verify").then((r) => r.data),
  // Admin RBAC — who am I? Returns { role, username, user_id, auth_kind }.
  adminMe: () => client.get("/admin/me").then((r) => r.data),
  // Admin staff management (only Admin Geral).
  listAdminStaff: () => client.get("/admin/staff").then((r) => r.data),
  createAdminStaff: (body) => client.post("/admin/staff", body).then((r) => r.data),
  updateAdminStaffPassword: (id, password) =>
    client.patch(`/admin/staff/${id}/password`, { password }).then((r) => r.data),
  // Iter307.3 — Senha provisória para usuário regular (db.users).
  // Somente Admin Geral. Retorna a senha em claro UMA vez para o admin
  // repassar por WhatsApp/telefone; força must_change_password=True.
  generateUserTempPassword: (userId) =>
    client.post(`/admin/users/${encodeURIComponent(userId)}/temp-password`).then((r) => r.data),
  toggleAdminStaffActive: (id, active) =>
    client.patch(`/admin/staff/${id}/active?active=${active ? "true" : "false"}`).then((r) => r.data),
  deleteAdminStaff: (id) => client.delete(`/admin/staff/${id}`).then((r) => r.data),
  // Iter218 — self-service change password (must_change_password flow).
  changeMyPassword: (body) =>
    client.post("/admin/me/change-password", body).then((r) => r.data),
  // Hearts (requires user auth for POST; GET works anonymously)
  getAuthorHeart: (slug) => client.get(`/authors/${encodeURIComponent(slug)}/heart`, { withCredentials: true }).then((r) => r.data),
  toggleAuthorHeart: (slug) => client.post(`/authors/${encodeURIComponent(slug)}/heart`, {}, { withCredentials: true }).then((r) => r.data),
  // Shorts (public list + admin CRUD)
  listShorts: (params = {}) => client.get("/shorts", { params }).then((r) => r.data),
  getShort: (id) => client.get(`/shorts/${id}`).then((r) => r.data),
  createShort: (body) => client.post("/shorts", body).then((r) => r.data),
  updateShort: (id, body) => client.put(`/shorts/${id}`, body).then((r) => r.data),
  deleteShort: (id) => client.delete(`/shorts/${id}`).then((r) => r.data),
  listAuthorShorts: (slug) => client.get(`/authors/${encodeURIComponent(slug)}/shorts`).then((r) => r.data),
  syncShortsFromChannel: (channel_id = null, max_scan = 100) => client.post("/shorts/sync-channel", { channel_id, max_scan }, { timeout: 300000 }).then((r) => r.data),
  // Bulk tag import (CSV per podcast)
  importTagsCsv: (podcastSlug, file, onProgress) => {
    const form = new FormData();
    form.append("podcast_slug", podcastSlug);
    form.append("file", file);
    return client.post("/admin/import-tags-csv", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (ev) => {
        if (onProgress && ev.total) onProgress(Math.round((ev.loaded / ev.total) * 100));
      },
      timeout: 180000,
    }).then((r) => r.data);
  },
  ensureTagsFromEpisodes: () => client.post("/admin/tags/ensure-from-episodes", {}, { timeout: 60000 }).then((r) => r.data),
  // Rank-change notifications (creator engagement loop)
  checkRankChanges: (period = "month", dry_run = false) =>
    client.post(`/admin/badges/check-rank-changes?period=${period}&dry_run=${dry_run}`, {}, { timeout: 120000 }).then((r) => r.data),
  updateNotifyPrefs: (notify_rank_changes) =>
    client.put("/users/me/notification-preferences", { notify_rank_changes }, { withCredentials: true }).then((r) => r.data),
  // Moderators (admin-only)
  listModerators: () => client.get("/admin/moderators").then((r) => r.data),
  setModerator: (email, is_moderator) => client.post("/admin/moderators", { email, is_moderator }).then((r) => r.data),
  // Newsletter campaigns
  newsletterRecentEpisodes: (days = 30, limit = 50) =>
    client.get(`/admin/newsletter/recent-episodes?days=${days}&limit=${limit}`).then((r) => r.data),
  newsletterListDrafts: () => client.get("/admin/newsletter/drafts").then((r) => r.data),
  newsletterGetDraft: (id) => client.get(`/admin/newsletter/drafts/${id}`).then((r) => r.data),
  newsletterSaveDraft: (body, id = null) => {
    const url = id ? `/admin/newsletter/draft?id=${id}` : "/admin/newsletter/draft";
    return client.post(url, body).then((r) => r.data);
  },
  newsletterDeleteDraft: (id) => client.delete(`/admin/newsletter/drafts/${id}`).then((r) => r.data),
  newsletterPreview: (body) => client.post("/admin/newsletter/preview", body).then((r) => r.data),
  newsletterSend: (id, test_only = null) => {
    const qs = test_only ? `?test_only=${encodeURIComponent(test_only)}` : "";
    return client.post(`/admin/newsletter/send/${id}${qs}`, {}, { timeout: 120000 }).then((r) => r.data);
  },
  newsletterCloneDraft: (id) => client.post(`/admin/newsletter/drafts/${id}/clone`).then((r) => r.data),
  // Outreach lists (separate audience for personal-style invites)
  outreachListsList: () => client.get("/admin/outreach/lists").then((r) => r.data),
  outreachListGet: (id) => client.get(`/admin/outreach/lists/${id}`).then((r) => r.data),
  outreachListCreate: (body) => client.post("/admin/outreach/lists", body).then((r) => r.data),
  outreachListUpdate: (id, body) => client.put(`/admin/outreach/lists/${id}`, body).then((r) => r.data),
  outreachListDelete: (id) => client.delete(`/admin/outreach/lists/${id}`).then((r) => r.data),
  // v3.33 — Brevo bridge (uses the already-synced professor_contacts).
  outreachProfessorsPreview: (params = {}) =>
    client.get("/admin/outreach/professors-preview", { params }).then((r) => r.data),
  outreachListImportBrevo: (body) =>
    client.post("/admin/outreach/lists/import-brevo", body).then((r) => r.data),
  // Secondary emails on the user account
  addSecondaryEmail: (email) =>
    client.post("/users/me/secondary-emails", { email }, { withCredentials: true }).then((r) => r.data),
  removeSecondaryEmail: (email) =>
    client.delete(`/users/me/secondary-emails/${encodeURIComponent(email)}`, { withCredentials: true }).then((r) => r.data),
  refreshPublicSlug: () =>
    client.post("/users/me/refresh-public-slug", {}, { withCredentials: true }).then((r) => r.data),
  // Subscribers / users admin
  adminListSubscribers: () => client.get("/newsletter/subscribers").then((r) => r.data),
  adminDeleteSubscriber: (email) => client.delete(`/admin/subscribers/${encodeURIComponent(email)}`).then((r) => r.data),
  newsletterResendConfirmations: (exclude_suspicious = true, dry_run = false) =>
    client.post("/admin/newsletter/resend-confirmations", { exclude_suspicious, dry_run }).then((r) => r.data),
  newsletterSuspicious: () => client.get("/admin/newsletter/suspicious").then((r) => r.data),
  newsletterDeleteBulk: (emails) => client.post("/admin/newsletter/delete-bulk", { emails }).then((r) => r.data),
  newsletterReminderSettingsGet: () => client.get("/admin/newsletter/reminder-settings").then((r) => r.data),
  newsletterReminderSettingsSet: (enabled) => client.put("/admin/newsletter/reminder-settings", { enabled }).then((r) => r.data),
  newsletterDeliveryReport: () => client.get("/admin/newsletter/delivery-report").then((r) => r.data),
  adminListUsers: (limit = 500) => client.get(`/admin/users?limit=${limit}`).then((r) => r.data),
  adminDeleteUser: (userId) => client.delete(`/admin/users/${encodeURIComponent(userId)}`).then((r) => r.data),
  adminUpdateUser: (userId, body) =>
    client.put(`/admin/users/${encodeURIComponent(userId)}`, body).then((r) => r.data),
  adminPromoteProfessor: (userId, approve = true) =>
    client.post(`/admin/users/${encodeURIComponent(userId)}/promote-professor`, { approve }).then((r) => r.data),
  // Autorizações de incorporação de obras (admin) — situação do aceite,
  // exportação CSV e fila interna de pedidos (revogação/retirada/correção).
  adminWorkAuthorizations: () => client.get("/admin/work-authorizations").then((r) => r.data),
  adminWorkAuthRequests: (status = "open") =>
    client.get("/admin/work-authorization-requests", { params: { status } }).then((r) => r.data),
  adminResolveWorkAuthRequest: (id) =>
    client.post(`/admin/work-authorization-requests/${encodeURIComponent(id)}/resolve`).then((r) => r.data),
  adminExportWorkAuthorizationsCsv: () =>
    client.get("/admin/work-authorizations/export.csv", { responseType: "blob" }).then((r) => r.data),
  adminCronHealth: () => client.get("/admin/cron/health").then((r) => r.data),
  cronRun: () => client.post("/admin/cron/run-now").then((r) => r.data),
  // Listas customizadas (admin) — agrupa cadastrados em listas privadas
  // para campanhas de e-mail externas. CRUD de listas + assign/unassign
  // por usuário + endpoint dedicado pra copiar todos os e-mails.
  adminListUserTags: () => client.get("/admin/user-tags").then((r) => r.data),
  adminCreateUserTag: (body) => client.post("/admin/user-tags", body).then((r) => r.data),
  adminUpdateUserTag: (slug, body) =>
    client.patch(`/admin/user-tags/${encodeURIComponent(slug)}`, body).then((r) => r.data),
  adminDeleteUserTag: (slug) =>
    client.delete(`/admin/user-tags/${encodeURIComponent(slug)}`).then((r) => r.data),
  adminAssignUserTag: (slug, userId, action /* add | remove */) =>
    client.post(`/admin/user-tags/${encodeURIComponent(slug)}/assign`, { user_id: userId, action }).then((r) => r.data),
  adminGetUserTagEmails: (slug) =>
    client.get(`/admin/user-tags/${encodeURIComponent(slug)}/emails`).then((r) => r.data),
  // Public ranking for /comunidade — sem números, só ordem
  communityRankings: (period = "month", top = 10, includeEpisodes = true) =>
    client.get(`/community/rankings?period=${period}&top=${top}&include_episodes=${includeEpisodes}`).then((r) => r.data),
  adminSendListEmail: (slug, payload) =>
    client.post(`/admin/user-tags/${encodeURIComponent(slug)}/send-email`, payload).then((r) => r.data),
  adminListListDispatches: (slug) =>
    client.get(`/admin/user-tags/${encodeURIComponent(slug)}/dispatches`).then((r) => r.data),
  // Episode recommendations
  episodeNextUp: (id, limit = 3) => client.get(`/episodes/${id}/next-up?limit=${limit}`).then((r) => r.data),
  // Colunistas (artigos PDF)
  listColumnists: (params = {}) => client.get("/colunistas", { params }).then((r) => r.data),
  // Iter302.6 — Amostra uniforme dos ~1869 artigos, via $sample.
  // Alimenta a rotação por visita da /artigos com cobertura real
  // do acervo completo, não só das 3 páginas iniciais.
  randomColumnists: ({ scope, size = 24 } = {}) =>
    client.get("/colunistas/random", { params: { scope, size } }).then((r) => r.data),
  listColumnistsAuthors: (params = {}) => client.get("/colunistas/authors", { params }).then((r) => r.data),

  // Podcast share-card cover (used by /api/og/podcast/{slug}).
  uploadPodcastCover: (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/admin/podcasts/${encodeURIComponent(slug)}/cover`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  getColumnistArticle: (author, slug) => client.get(`/colunistas/${author}/${slug}`).then((r) => r.data),
  sendModeratorDigest: (dry_run = false) =>
    client.post(`/admin/moderators/send-pending-digest?dry_run=${dry_run}`, {}, { timeout: 60000 }).then((r) => r.data),
  // Comment reports (community-driven moderation)
  reportComment: (comment_id, reason = "") =>
    client.post(`/comments/${comment_id}/report`, { reason }, { withCredentials: true }).then((r) => r.data),
  listCommentReports: (status = "pending", limit = 100) =>
    client.get("/admin/comment-reports", { params: { status, limit }, withCredentials: true }).then((r) => r.data),
  pendingCommentReports: () =>
    client.get("/admin/comment-reports/pending-count", { withCredentials: true }).then((r) => r.data),
  reviewCommentReport: (report_id, action, ban_reason = "") =>
    client.post(`/admin/comment-reports/${report_id}/review`, { action, ban_reason }, { withCredentials: true }).then((r) => r.data),
  categoriesCoverage: () => client.get("/admin/categories/coverage").then((r) => r.data),
  // Hierarchical category tree
  categoriesTree: () => client.get("/categories/tree").then((r) => r.data),
  getCategory: (cid) => client.get(`/categories/${cid}`).then((r) => r.data),
  autoCategorizeAll: (only_uncategorized = true, limit = 200) =>
    client.post("/admin/episodes/auto-categorize", { only_uncategorized, limit }, { timeout: 600000 }).then((r) => r.data),
  autoCategorizeOne: (episode_id) =>
    client.post(`/admin/episodes/${episode_id}/auto-categorize`, {}, { timeout: 60000 }).then((r) => r.data),
  // Professor status & endorsement
  requestProfessor: () => client.post("/users/me/request-professor", {}, { withCredentials: true }).then((r) => r.data),
  listPendingProfessors: () => client.get("/admin/professors/pending").then((r) => r.data),
  approveProfessor: (user_id) => client.post(`/admin/professors/${user_id}/approve`).then((r) => r.data),
  revokeProfessor: (user_id) => client.post(`/admin/professors/${user_id}/revoke`).then((r) => r.data),
  // PDF attachments (collection episode)
  uploadCollectionEpisodePdf: (collection_id, episode_id, file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return client.post(`/collections/${collection_id}/episodes/${episode_id}/pdf`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
      onUploadProgress: (ev) => {
        if (onProgress && ev.total) onProgress(Math.round((ev.loaded / ev.total) * 100));
      },
      timeout: 120000,
    }).then((r) => r.data);
  },
  deleteCollectionEpisodePdf: (collection_id, episode_id) =>
    client.delete(`/collections/${collection_id}/episodes/${episode_id}/pdf`, { withCredentials: true }).then((r) => r.data),
  sharedCollectionPdfUrl: (share_slug, episode_id) =>
    `${API}/share/${share_slug}/episodes/${episode_id}/pdf`,
};

export default api;
